import { useCallback, useEffect, useState } from 'react';
import type { Revenue, RevenueAttachment } from '../types/finance';
import { getRevenueAttachments, uploadRevenueAttachment, getRevenueAttachmentDownloadUrl, getRevenue } from '../lib/api/endpoints';
import { revenueAttachmentFromDto, revenueFromDto } from '../lib/api/mappers';
import { comprimirRecibo } from '../utils/receiptImage';

/**
 * FE-32 — las versiones del soporte de un ingreso (tiquete de báscula,
 * remesa), su subida y su descarga. Mismo contrato que `useExpenseReceipts`:
 * se versiona, la URL se pide AL ABRIR y `subir` devuelve el INGRESO recargado
 * para que la fila deje de decir «sin soporte».
 */
export function useRevenueAttachments(revenueId: string | null) {
  const [attachments, setAttachments] = useState<RevenueAttachment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [subiendo, setSubiendo] = useState(false);

  useEffect(() => {
    if (!revenueId) {
      setAttachments([]);
      setError(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setAttachments([]);
      try {
        const res = await getRevenueAttachments(revenueId);
        if (cancelled) return;
        setAttachments(res.items.map(revenueAttachmentFromDto));
        setError(null);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err : new Error('Error desconocido cargando los soportes'));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [revenueId]);

  const subir = useCallback(async (file: File): Promise<Revenue> => {
    if (!revenueId) throw new Error('No hay ingreso al que adjuntar el soporte.');
    setSubiendo(true);
    try {
      const listo = await comprimirRecibo(file);
      const soporte = revenueAttachmentFromDto(await uploadRevenueAttachment(revenueId, listo));
      setAttachments(prev => [soporte, ...prev]);
      return revenueFromDto(await getRevenue(revenueId));
    } finally {
      setSubiendo(false);
    }
  }, [revenueId]);

  const urlDeDescarga = useCallback(async (attachmentId: string): Promise<string> => {
    if (!revenueId) throw new Error('No hay ingreso del que descargar el soporte.');
    const res = await getRevenueAttachmentDownloadUrl(revenueId, attachmentId);
    return res.url;
  }, [revenueId]);

  return { attachments, isLoading, error, subiendo, subir, urlDeDescarga };
}
